import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { config } from "@/config";
import { SettingsApi } from "@/api/giftcard.api";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import { getFormattedDateTime } from "@/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/ui/data-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  CreditCard,
  Shield,
  AlertCircle,
  CheckCircle,
  History,
  Eye,
  Filter,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { GiftCardApi } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { PaginationControls } from "@/components/common/PaginationControl";

interface RedeemFormData {
  merchantCode: string;
  phoneNumber: string;
  cardNumber: string;
  amount: string;
  invoiceNo: string;
}

// Yup validation schema
const validationSchema = Yup.object({
  merchantCode: Yup.string().required("Brand is required"),
  phoneNumber: Yup.string().required("Phone number is required"),
  cardNumber: Yup.string().required("Gift card number is required"),
  amount: Yup.string()
    .required("Amount is required")
    .test(
      "is-positive-integer",
      "Amount must be a whole number greater than 0",
      (value) => {
        if (!value) return false;
        const numValue = parseInt(value, 10);
        return (
          !isNaN(numValue) && numValue > 0 && value === numValue.toString()
        );
      }
    ),
  invoiceNo: Yup.string()
    .required("Invoice number is required")
    .matches(
      /^[A-Za-z0-9]+$/,
      "Invoice number must be alphanumeric (letters and numbers only)"
    ),
});

interface VerifiedCardData {
  name: string;
  email: string;
  issuedAmount: number;
  usedAmount: number;
  availableBalance: number;
  expiryDate: string;
  isPartial?: boolean;
}

interface RedeemHistoryItem {
  id: number;
  issuedCardNumber: string | null;
  amountDeducted: number;
  redemedDate: string;
  giftCardStatus: string;
  mobileNo: string;
  mallId: number;
  merchantCode: string | null;
  merchantName: string;
  transactionId: string | null;
  brandName: string | null;
  invoiceNo: string | null;
}

const RedeemGiftCard = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isCardVerified, setIsCardVerified] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [otp, setOtp] = useState("");
  const [verifiedCardData, setVerifiedCardData] =
    useState<VerifiedCardData | null>(null);
  const [cardNumber, setCardNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [redemption, setRedemption] = useState<{
    cardNumber: string;
    amount: string;
    redeemedAt: Date;
  } | null>(null);

  // Filter states for redemption history / mall-scoped redeem
  const [selectedMallId, setSelectedMallId] = useState<string>("");
  const [selectedMerchantCode, setSelectedMerchantCode] = useState<string>("");
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("redeem");
  const [historyTabInitialized, setHistoryTabInitialized] = useState(false);
  const [merchantOptions, setMerchantOptions] = useState<
    { id: number | string; businessName: string; merchantCode: string }[]
  >([]);
  const [isMerchantsLoading, setIsMerchantsLoading] = useState(false);

  const [mallOptions, setMallOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [isMallOptionsLoading, setIsMallOptionsLoading] = useState(false);

  // State for redeem history data from API
  const [redeemHistory, setRedeemHistory] = useState<RedeemHistoryItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalGiftCards, setTotalGiftCards] = useState(0);

  // State for history modal
  const [selectedHistoryItem, setSelectedHistoryItem] =
    useState<RedeemHistoryItem | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const initialValues: RedeemFormData = {
    merchantCode: "",
    phoneNumber: "",
    cardNumber: "",
    amount: "",
    invoiceNo: "",
  };

  const fetchMerchantOptions = useCallback(
    async (mallIdParam?: number) => {
      const mallId = mallIdParam ?? (selectedMallId ? parseInt(selectedMallId, 10) : NaN);
      if (!mallId || Number.isNaN(mallId)) {
        setMerchantOptions([]);
        return;
      }

      setIsMerchantsLoading(true);
      try {
        const userInfoRaw = sessionStorage.getItem("userInfo");
        let userId = "";
        if (userInfoRaw) {
          try {
            const parsed = JSON.parse(userInfoRaw);
            userId =
              (parsed && (parsed.id ?? parsed.userId ?? ""))?.toString() ?? "";
          } catch (error) {
            console.warn(
              "Failed to parse userInfo from session storage",
              error
            );
          }
        }

        const response = await fetch(
          `${config.GIFT_API_BASE_URL}/Merchant/GetAllMerchant`,
          {
            method: "POST",
            headers: {
              accept: "text/plain",
              "Content-Type": "application/json",
              XApiKey: config.GIFT_API_KEY,
            },
            body: JSON.stringify({
              p_UserId: userId,
              p_PageNumber: 1,
              p_PageSize: 50,
              p_MallId: mallId,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch merchants");
        }

        const data = await response.json();

        if (data.error_msg) {
          toast.error(data.error_msg || "Failed to fetch merchants");
          setMerchantOptions([]);
          return;
        }

        const merchants = data?.merchants;
        if (Array.isArray(merchants) && merchants.length > 0) {
          const mapped = merchants
            .filter((merchant: any) => merchant?.isActive)
            .map((merchant: any, index: number) => ({
              id: merchant.id ?? `merchant-${index}`,
              businessName: merchant.businessName || "Unknown Brand",
              merchantCode: merchant.merchantCode || "",
            }));
          setMerchantOptions(mapped);
        } else {
          setMerchantOptions([]);
        }
      } catch (error) {
        console.error("Error loading merchants:", error);
        setMerchantOptions([]);
        toast.error("Failed to load brands");
      } finally {
        setIsMerchantsLoading(false);
      }
    },
    [selectedMallId]
  );

  const fetchMallOptions = useCallback(async () => {
    setIsMallOptionsLoading(true);
    try {
      const data = await SettingsApi.getMallDetails();
      const malls = Array.isArray(data?.malldetails) ? data.malldetails : [];
      const mappedMalls = malls.map((mall: any) => ({
        id: mall.mallId,
        name: mall.mallName || `Mall ${mall.mallId}`,
      }));
      setMallOptions(mappedMalls);
      if (mappedMalls.length > 0) {
        setSelectedMallId(mappedMalls[0].id.toString());
      }
    } catch (error) {
      console.error("Error fetching mall options:", error);
      setMallOptions([]);
      toast.error("Failed to load malls");
    } finally {
      setIsMallOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMallOptions();
  }, [fetchMallOptions]);

  // Fetch merchants whenever mall is selected (redeem + history)
  useEffect(() => {
    if (selectedMallId) {
      fetchMerchantOptions(parseInt(selectedMallId, 10));
    }
  }, [selectedMallId, fetchMerchantOptions]);

  const handleSubmit = async (
    values: RedeemFormData,
    { resetForm }: FormikHelpers<RedeemFormData>
  ) => {
    setIsSubmitting(true);

    try {
      if (!selectedMallId) {
        toast.error("Please select a mall");
        setIsSubmitting(false);
        return;
      }

      const isPartial = verifiedCardData?.isPartial ?? true;

      // Validate OTP is entered
      if (!otp || !otp.trim()) {
        toast.error("Please enter OTP");
        setIsSubmitting(false);
        return;
      }

      if (
        isPartial &&
        (!values.amount ||
          isNaN(parseFloat(values.amount)) ||
          parseFloat(values.amount) <= 0)
      ) {
        toast.error("Please enter a valid redemption amount");
        setIsSubmitting(false);
        return;
      }

      const amountToRedeem = isPartial
        ? parseFloat(values.amount)
        : verifiedCardData?.availableBalance ?? 0;

      if (!amountToRedeem || amountToRedeem <= 0) {
        toast.error("No balance available to redeem");
        setIsSubmitting(false);
        return;
      }

      // Get username from sessionStorage
      const userInfo = sessionStorage.getItem("userInfo");
      const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
      const userName = parsedUserInfo?.username || "";

      const otpValue = parseInt(otp.trim(), 10);
      if (Number.isNaN(otpValue)) {
        toast.error("Please enter a valid OTP");
        setIsSubmitting(false);
        return;
      }

      const redemptionPayload = {
        p_IssuedGiftCardNo: values.cardNumber.replace(/\s+/g, ""),
        p_RedemedDate: new Date().toISOString(),
        p_MallId: parseInt(selectedMallId, 10),
        p_MerchantCode: values.merchantCode,
        p_AmountToRedeem: amountToRedeem,
        p_OTP: otpValue,
        p_InvoiceNo: values.invoiceNo,
        p_UserName: userName,
      };

      const response = await GiftCardApi.giftCardRedemption(redemptionPayload);

      // Check for error message in response
      if (
        response &&
        response.error_msg &&
        typeof response.error_msg === "string" &&
        response.error_msg.trim() !== ""
      ) {
        toast.error(response.error_msg);
        setIsSubmitting(false);
        return;
      }

      // If no error or error_msg is empty, proceed with success
      // Store redemption data and open success modal
      setRedemption({
        cardNumber: values.cardNumber.replace(/\s+/g, ""),
        amount: amountToRedeem.toString(),
        redeemedAt: new Date(),
      });
      // Reset form and clear verification/OTP state so details are hidden
      resetForm();
      setIsCardVerified(false);
      setIsOtpSent(false);
      setOtp("");
      setVerifiedCardData(null);
      setCardNumber("");
      setPhoneNumber("");

      setIsSuccessModalOpen(true);
      toast.success("Gift card redeemed successfully!");
    } catch (error) {
      console.error("Error redeeming gift card:", error);
      const errorMessage = HandleError(error);
      toast.error(
        errorMessage || "Failed to redeem gift card. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = (resetForm?: () => void) => {
    if (resetForm) {
      resetForm();
    }
    setIsSubmitting(false);
    setIsVerifying(false);
    setIsVerifyingOtp(false);
    setIsSuccessModalOpen(false);
    setIsCardVerified(false);
    setIsOtpSent(false);
    setOtp("");
    setVerifiedCardData(null);
    setCardNumber("");
    setPhoneNumber("");
    setRedemption(null);
  };

  const handleVerifyCard = async (
    values: RedeemFormData,
    setFieldValue: (field: string, value: any) => void
  ) => {
    // Validate all required fields
    if (!values.merchantCode?.trim()) {
      toast.error("Please enter merchant code");
      return;
    }

    if (!values.phoneNumber?.trim()) {
      toast.error("Please enter a phone number");
      return;
    }

    if (!values.cardNumber?.trim()) {
      toast.error("Please enter a card number");
      return;
    }

    setIsVerifying(true);
    try {
      // Store card number and phone number for later use
      const sanitizedCardNumber = values.cardNumber.replace(/\s+/g, "");
      setCardNumber(sanitizedCardNumber);
      setPhoneNumber(values.phoneNumber);

      const amountStr = values.amount ? String(values.amount).trim() : "";
      if (!amountStr) {
        toast.error("Amount is required");
        setIsVerifying(false);
        return;
      }

      const amountValue = parseFloat(amountStr);
      if (isNaN(amountValue) || amountValue <= 0) {
        toast.error("Please enter a valid amount");
        setIsVerifying(false);
        return;
      }

      const apiPayload = {
        p_CardNumber: sanitizedCardNumber,
        p_MobileNo: values.phoneNumber,
        p_AmountToRedeem: amountValue,
      };

      // Call API to get gift card details
      const response = await GiftCardApi.getGiftCardDetailsByCardNumber(
        apiPayload
      );

      // Check for error message in response - if error_msg has a value, show error
      if (
        response &&
        response.error_msg &&
        typeof response.error_msg === "string" &&
        response.error_msg.trim() !== ""
      ) {
        toast.error(response.error_msg);
        setIsCardVerified(false);
        setIsOtpSent(false);
        setVerifiedCardData(null);
        return;
      }

      // If no error or error_msg is empty, proceed to show details
      if (response && response.cardDetails) {
        const cardDetails =
          response.cardDetails as typeof response.cardDetails & {
            isPartial?: boolean;
          };
        const partialFlag = cardDetails?.isPartial ?? true;

        // Card verified - set card data immediately
        setVerifiedCardData({
          name: cardDetails.name,
          email: cardDetails.email,
          issuedAmount: cardDetails.issuedAmount,
          usedAmount: cardDetails.usedAmount,
          availableBalance: cardDetails.availableBalance,
          expiryDate: cardDetails.expiryDate,
          isPartial: partialFlag,
        });

        // For full redemption, set amount to available balance if not already set
        // For partial redemption, keep user's input (amount is mandatory)
        const currentAmountStr = values.amount
          ? String(values.amount).trim()
          : "";
        if (!partialFlag && !currentAmountStr) {
          setFieldValue(
            "amount",
            cardDetails.availableBalance?.toString() ?? ""
          );
        }
        // For partial cards, keep the user's entered amount (don't clear it)

        if (partialFlag) {
          toast.success("Card verified! OTP sent to your phone number!");
        } else {
          toast.success("Card verified! Full redemption available.");
        }

        setIsCardVerified(true);
        setIsOtpSent(true);
      } else {
        toast.error(
          "Failed to verify gift card. Please check the card number and phone number."
        );
        setIsCardVerified(false);
        setIsOtpSent(false);
        setVerifiedCardData(null);
      }
    } catch (error) {
      console.error("Error verifying gift card:", error);
      const errorMessage = HandleError(error);
      toast.error(
        errorMessage ||
          "Failed to verify gift card. Please check the card number and phone number."
      );
      setIsCardVerified(false);
      setIsOtpSent(false);
      setVerifiedCardData(null);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      toast.error("Please enter OTP");
      return;
    }

    if (!cardNumber || !phoneNumber) {
      toast.error("Please verify card first");
      return;
    }

    setIsVerifyingOtp(true);
    try {
      // TODO: Replace with actual OTP verification API call
      // For now, validate against hardcoded OTP "1992"
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (otp === "1992") {
        // OTP verified successfully - card details already set from handleVerifyCard
        setIsOtpVerified(true);
        toast.success("OTP verified successfully!");
      } else {
        toast.error("Invalid OTP. Please try again.");
        setIsOtpVerified(false);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      const errorMessage = HandleError(error);
      toast.error(errorMessage || "Invalid OTP. Please try again.");
      setIsOtpVerified(false);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Fetch redemption history data with backend pagination
  const fetchRedemptionHistory = useCallback(
    async (options?: { page?: number; pageSize?: number }) => {
      if (!selectedMallId) {
        setRedeemHistory([]);
        setTotalGiftCards(0);
        return;
      }

      setIsLoadingHistory(true);
      try {
        const currentPage = options?.page ?? page;
        const currentPageSize = options?.pageSize ?? rowsPerPage;

        const response = await GiftCardApi.getRedemptionReportData({
          p_MallId: parseInt(selectedMallId, 10),
          p_MerchantCode: selectedMerchantCode,
          p_Page: currentPage + 1, // Backend expects 1-based page
          p_PageSize: currentPageSize,
        });

        // Map API response to RedeemHistoryItem format
        if (
          response &&
          response.reportResponses &&
          Array.isArray(response.reportResponses)
        ) {
          const mappedData: RedeemHistoryItem[] = response.reportResponses.map(
            (item: any) => ({
              id: item.id || 0,
              issuedCardNumber: item.issuedCardNumber || null,
              amountDeducted: item.amountDeducted || 0,
              redemedDate: item.redemedDate || new Date().toISOString(),
              giftCardStatus: item.giftCardStatus || "N/A",
              mobileNo: item.mobileNo || "",
              mallId: item.mallId || 0,
              merchantCode: item.brandCode || item.merchantCode || null,
              merchantName: item.brandName || item.merchantName || "",
              transactionId: item.transactionId || null,
              brandName: item.brandName || null,
              invoiceNo: item.invoiceNo || null,
            })
          );
          setRedeemHistory(mappedData);
          // API may return totalCount as a string
          const total =
            Number(response.totalCount) || mappedData.length || 0;
          setTotalGiftCards(total);
        } else {
          // If no data, set empty array
          setRedeemHistory([]);
          setTotalGiftCards(0);
        }
      } catch (error) {
        console.error("Error fetching redemption history:", error);
        const errorMessage = HandleError(error);
        toast.error(errorMessage || "Failed to fetch redemption history.");
        // Keep existing data on error
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [selectedMallId, selectedMerchantCode, page, rowsPerPage]
  );

  // Initialize history tab when first accessed
  useEffect(() => {
    if (activeTab === "history" && !historyTabInitialized) {
      setPage(0);
      setHistoryTabInitialized(true);
    }
  }, [activeTab, historyTabInitialized]);

  // Fetch redemption history when filters change or when history tab is accessed
  useEffect(() => {
    if (activeTab === "history" && historyTabInitialized) {
      // Reset to first page when filters change
      setPage(0);
      fetchRedemptionHistory({ page: 0, pageSize: rowsPerPage });
    }
  }, [
    activeTab,
    selectedMallId,
    selectedMerchantCode,
    historyTabInitialized,
    rowsPerPage,
  ]);

  // Pagination - use backend totalCount for accurate page count
  const totalPages = Math.max(
    1,
    Math.ceil((totalGiftCards || 0) / (rowsPerPage || 1))
  );

  // Centralized handler for page changes (0-based index)
  const handlePageChange = (newPage: number) => {
    if (newPage < 0 || newPage > totalPages - 1) return;
    setPage(newPage);
    fetchRedemptionHistory({ page: newPage, pageSize: rowsPerPage });
  };

  // Handle view history details
  const handleViewHistoryDetails = (item: RedeemHistoryItem) => {
    setSelectedHistoryItem(item);
    setIsHistoryModalOpen(true);
  };

  // Get status badge for history
  const getStatusBadge = (status: string) => {
    const statusLower = status.toLowerCase();
    let color = "bg-blue-100 text-blue-800";
    let text = status;

    if (
      statusLower.includes("successfully") ||
      statusLower.includes("redeemed")
    ) {
      color = "bg-green-100 text-green-800";
    } else if (statusLower.includes("partially")) {
      color = "bg-yellow-100 text-yellow-800";
    } else if (
      statusLower.includes("failed") ||
      statusLower.includes("error")
    ) {
      color = "bg-red-100 text-red-800";
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {text}
      </span>
    );
  };

  // Columns for redeem history table
  const historyColumns = [
    {
      header: "Card Number",
      accessorKey: "issuedCardNumber" as keyof RedeemHistoryItem,
      cell: (row: RedeemHistoryItem) => (
        <div className="flex items-center space-x-2">
          <CreditCard className="h-4 w-4 text-gray-500" />
          <span className="font-mono text-sm">
            {row.issuedCardNumber || "N/A"}
          </span>
        </div>
      ),
    },
    {
      header: "Amount Deducted",
      accessorKey: "amountDeducted" as keyof RedeemHistoryItem,
      cell: (row: RedeemHistoryItem) => {
        const currency = localStorage.getItem("giftCardCurrency") || "USD";
        return (
          <span className="font-medium text-red-600">
            {row.amountDeducted.toFixed(2)} {currency}
          </span>
        );
      },
    },
    {
      header: "Mobile Number",
      accessorKey: "mobileNo" as keyof RedeemHistoryItem,
      cell: (row: RedeemHistoryItem) => (
        <span className="text-gray-900">{row.mobileNo || "N/A"}</span>
      ),
    },
    {
      header: "Brand Name",
      accessorKey: "brandName" as keyof RedeemHistoryItem,
      cell: (row: RedeemHistoryItem) => (
        <span className="text-gray-900">{row.brandName || "N/A"}</span>
      ),
    },
    {
      header: "Date & Time",
      accessorKey: "redemedDate" as keyof RedeemHistoryItem,
      cell: (row: RedeemHistoryItem) => (
        <div className="text-sm">
          {getFormattedDateTime(row.redemedDate) || "N/A"}
        </div>
      ),
    },
    {
      header: "Transaction ID",
      accessorKey: "transactionId" as keyof RedeemHistoryItem,
      cell: (row: RedeemHistoryItem) => (
        <span className="text-gray-900">{row.transactionId || "N/A"}</span>
      ),
    },
    {
      header: "Invoice ID",
      accessorKey: "invoiceNo" as keyof RedeemHistoryItem,
      cell: (row: RedeemHistoryItem) => (
        <span className="text-gray-900">{row.invoiceNo || "N/A"}</span>
      ),
    },
    {
      header: "Actions",
      id: "actions",
      cell: (row: RedeemHistoryItem) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleViewHistoryDetails(row)}
          className="h-8 px-3"
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Redeem Card</h1>
          <p className="text-gray-600 mt-1">
            Process gift card redemptions and view history
          </p>
        </div>
        <div className="w-48">
          <Select
            value={selectedMallId || undefined}
            onValueChange={(value) => {
              setSelectedMallId(value);
              setSelectedMerchantCode("");
              setIsCardVerified(false);
              setIsOtpSent(false);
              setOtp("");
              setVerifiedCardData(null);
              setCardNumber("");
              setPhoneNumber("");
            }}
            disabled={isMallOptionsLoading || mallOptions.length === 0}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  isMallOptionsLoading ? "Loading malls..." : "Select Mall"
                }
              />
            </SelectTrigger>
            <SelectContent className="!max-h-36 overflow-auto py-0">
              {mallOptions.length > 0 ? (
                mallOptions.map((mall) => (
                  <SelectItem key={mall.id} value={mall.id.toString()}>
                    {mall.name}
                  </SelectItem>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {isMallOptionsLoading ? "Loading..." : "No malls found"}
                </div>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        defaultValue="redeem"
        className="space-y-6"
      >
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="redeem" className="flex items-center space-x-2">
            <CreditCard className="h-4 w-4" />
            <span>Redeem Card</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center space-x-2">
            <History className="h-4 w-4" />
            <span>Redeem History</span>
          </TabsTrigger>
        </TabsList>

        {/* Redeem Gift Card Tab */}
        <TabsContent value="redeem" className="space-y-6">
          {/* Form */}
          <Formik
            key={selectedMallId || "no-mall"}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, resetForm }) => (
              <Form className="space-y-8">
                {/* Redemption Details Section */}
                <Card>
                  <CardHeader>
                    <CardTitle>Redemption Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Brand - at top */}
                    <div className="space-y-2">
                      <Label htmlFor="merchantCode">Brand *</Label>
                      <Field name="merchantCode">
                        {({ field, form, meta }: any) => (
                          <div>
                            <Select
                              value={field.value}
                              onValueChange={(value) =>
                                form.setFieldValue(field.name, value)
                              }
                              disabled={isMerchantsLoading}
                            >
                              <SelectTrigger
                                id="merchantCode"
                                className={
                                  meta.touched && meta.error
                                    ? "border-red-500"
                                    : ""
                                }
                              >
                                <SelectValue
                                  placeholder={
                                    isMerchantsLoading
                                      ? "Loading merchants..."
                                      : "Select Brand"
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent className="!max-h-64 overflow-auto py-0">
                                {merchantOptions.length > 0 ? (
                                  merchantOptions.map((merchant) => (
                                    <SelectItem
                                      key={merchant.id}
                                      value={merchant.merchantCode || merchant.businessName}
                                    >
                                      {merchant.businessName}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <div className="px-2 py-1 text-sm text-gray-500">
                                    {isMerchantsLoading
                                      ? "Loading..."
                                      : "No brands found"}
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                            {meta.touched && meta.error && (
                              <p className="text-red-500 text-sm mt-1">
                                {meta.error}
                              </p>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>

                    {/* Phone Number */}
                    <div className="space-y-2">
                      <Label htmlFor="phoneNumber">Phone Number *</Label>
                      <Field name="phoneNumber">
                        {({ field, meta }: any) => (
                          <div>
                            <Input
                              {...field}
                              id="phoneNumber"
                              type="tel"
                              placeholder="Enter phone number"
                              className={
                                meta.touched && meta.error
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {meta.touched && meta.error && (
                              <p className="text-red-500 text-sm mt-1">
                                {meta.error}
                              </p>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>

                    {/* Gift Card Number */}
                    <div className="space-y-2">
                      <Label htmlFor="cardNumber">Gift Card Number *</Label>
                      <Field name="cardNumber">
                        {({ field, meta }: any) => (
                          <div>
                            <Input
                              {...field}
                              id="cardNumber"
                              type="text"
                              placeholder="1234 5678 9101"
                              className={
                                meta.touched && meta.error
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {meta.touched && meta.error && (
                              <p className="text-red-500 text-sm mt-1">
                                {meta.error}
                              </p>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>

                    {/* Invoice Number */}
                    <div className="space-y-2">
                      <Label htmlFor="invoiceNo">Invoice Number *</Label>
                      <Field name="invoiceNo">
                        {({ field, meta, form }: any) => (
                          <div>
                            <Input
                              {...field}
                              id="invoiceNo"
                              type="text"
                              placeholder="Enter invoice number"
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow alphanumeric characters (letters and numbers)
                                if (
                                  value === "" ||
                                  /^[A-Za-z0-9]+$/.test(value)
                                ) {
                                  form.setFieldValue(field.name, value);
                                }
                              }}
                              className={
                                meta.touched && meta.error
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {meta.touched && meta.error && (
                              <p className="text-red-500 text-sm mt-1">
                                {meta.error}
                              </p>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>

                    {/* Amount to Redeem */}
                    <div className="space-y-2">
                      <Label htmlFor="amount">Amount to Redeem *</Label>
                      <Field name="amount">
                        {({ field, meta, form }: any) => (
                          <div>
                            <Input
                              {...field}
                              id="amount"
                              type="number"
                              placeholder="0"
                              min="1"
                              step="1"
                              disabled={isCardVerified}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow whole numbers (no decimals)
                                if (value === "" || /^\d+$/.test(value)) {
                                  form.setFieldValue(field.name, value);
                                }
                              }}
                              className={
                                meta.touched && meta.error
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            {meta.touched && meta.error && (
                              <p className="text-red-500 text-sm mt-1">
                                {meta.error}
                              </p>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>

                    {/* Quick Select Buttons - Show initially, hide after API call */}
                    {!isCardVerified && (
                      <div className="space-y-2">
                        <Label>Quick Select</Label>
                        <div className="flex flex-wrap gap-2">
                          {[10, 25, 50, 100, 200, 500].map((value) => (
                            <Button
                              key={value}
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setFieldValue("amount", value.toString())
                              }
                              className="min-w-[60px]"
                            >
                              {value}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Full Redemption Notice */}
                    {verifiedCardData &&
                      verifiedCardData.isPartial === false && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 space-y-2">
                          <p className="font-semibold">
                            Full redemption required
                          </p>
                          <p>
                            This gift card must be redeemed in full. The
                            available balance of{" "}
                            <span className="font-bold">
                              {verifiedCardData.availableBalance.toFixed(2)}{" "}
                              {localStorage.getItem("giftCardCurrency") ||
                                "USD"}
                            </span>{" "}
                            will be deducted automatically.
                          </p>
                        </div>
                      )}

                    {/* Gift Card Details - Show after successful verification */}
                    {isCardVerified && verifiedCardData && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                        <h3 className="text-sm font-semibold text-green-800">
                          Gift Card Details
                        </h3>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-gray-600">Name:</p>
                            <p className="font-medium text-gray-900">
                              {verifiedCardData.name}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Email:</p>
                            <p className="font-medium text-gray-900">
                              {verifiedCardData.email}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Issued Amount:</p>
                            <p className="font-bold text-green-600">
                              {verifiedCardData.issuedAmount.toFixed(2)}{" "}
                              {localStorage.getItem("giftCardCurrency") ||
                                "USD"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Used Amount:</p>
                            <p className="font-bold text-red-600">
                              {verifiedCardData.usedAmount.toFixed(2)}{" "}
                              {localStorage.getItem("giftCardCurrency") ||
                                "USD"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Available Balance:</p>
                            <p className="font-bold text-green-600">
                              {verifiedCardData.availableBalance.toFixed(2)}{" "}
                              {localStorage.getItem("giftCardCurrency") ||
                                "USD"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Expiry Date:</p>
                            <p className="font-medium text-gray-900">
                              {getFormattedDateTime(verifiedCardData.expiryDate) ||
                                "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* OTP Input Section - Show after card verification */}
                    {isCardVerified && (
                      <div className="space-y-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <h3 className="text-sm font-semibold text-yellow-800">
                          Enter OTP
                        </h3>
                        <p className="text-xs text-yellow-700">
                          An OTP has been sent to your phone number. Please
                          enter it below to proceed with redemption.
                        </p>
                        <Input
                          type="password"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder="Enter OTP"
                          value={otp}
                          onChange={(e) =>
                            setOtp(e.target.value.replace(/\D/g, ""))
                          }
                          className="w-full"
                          maxLength={6}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Submit Button */}
                <div className="flex justify-center pt-6">
                  <div className="flex items-center space-x-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleReset(resetForm)}
                      disabled={isSubmitting || isVerifying}
                      className="px-8"
                    >
                      Reset
                    </Button>
                    {!isCardVerified ? (
                      <Button
                        type="button"
                        onClick={() => handleVerifyCard(values, setFieldValue)}
                        disabled={
                          isVerifying ||
                          !values.merchantCode ||
                          !values.phoneNumber ||
                          !values.cardNumber ||
                          !values.invoiceNo ||
                          (verifiedCardData?.isPartial &&
                            (!values.amount ||
                              isNaN(parseFloat(values.amount)) ||
                              parseFloat(values.amount) <= 0))
                        }
                        className="bg-amber-600 hover:bg-amber-700 text-white px-8"
                      >
                        {isVerifying ? "Verifying..." : "Verify"}
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        className="bg-amber-600 hover:bg-amber-700 text-white px-8"
                        disabled={isSubmitting || !otp || !otp.trim()}
                      >
                        {isSubmitting ? "Processing..." : "Redeem Gift Card"}
                      </Button>
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </TabsContent>

        {/* Redeem History Tab */}
        <TabsContent value="history" className="space-y-6">
          {/* Filter Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Filter className="h-5 w-5" />
                <span>Filters</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Brand Dropdown */}
                <div className="space-y-2">
                  <Label htmlFor="merchantCode">Brand</Label>
                  <Select
                    value={selectedMerchantCode || "all"}
                    onValueChange={(value) =>
                      setSelectedMerchantCode(value === "all" ? "" : value)
                    }
                    disabled={isMerchantsLoading}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          isMerchantsLoading
                            ? "Loading merchants..."
                            : "Select Brand"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="!max-h-48 overflow-auto py-0">
                      <SelectItem value="all">All Brands</SelectItem>
                      {merchantOptions.map((merchant) => (
                        <SelectItem
                          key={merchant.id}
                          value={merchant.merchantCode || merchant.businessName}
                        >
                          {merchant.businessName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* History Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <History className="h-5 w-5" />
                <span>Redemption History</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingHistory ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500">Loading redemption history...</p>
                </div>
              ) : (
                <DataTable
                  title=""
                  data={redeemHistory}
                  columns={historyColumns}
                />
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          {!isLoadingHistory && (
            <div className="mt-4 flex justify-between items-center">
              {/* Rows per page selector */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Rows per page:</span>
                <Select
                  value={rowsPerPage.toString()}
                  onValueChange={(value) => {
                    const newRowsPerPage = parseInt(value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0); // Reset to first page when changing rows per page
                    fetchRedemptionHistory({
                      page: 0,
                      pageSize: newRowsPerPage,
                    });
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Pagination controls */}
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                onPrevHandler={() => handlePageChange(page - 1)}
                onNextHandler={() => handlePageChange(page + 1)}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Success Modal */}
      <Dialog open={isSuccessModalOpen} onOpenChange={setIsSuccessModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-green-600">
              <CheckCircle className="h-6 w-6" />
              <span>Gift Card Redeemed Successfully!</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <p className="text-gray-600 text-center">
              Your gift card redemption has been processed.
            </p>

            {redemption && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-6 border-2 border-emerald-200">
                <div className="bg-white rounded-lg shadow p-6 space-y-4">
                  {/* Amount */}
                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Amount Redeemed</div>
                    <div className="text-2xl font-bold text-green-600">
                      {parseFloat(redemption.amount).toFixed(2)}{" "}
                      {localStorage.getItem("giftCardCurrency") || "USD"}
                    </div>
                  </div>

                  {/* Date & Time */}
                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Date & Time</div>
                    <div className="font-medium text-gray-800">
                      {getFormattedDateTime(redemption.redeemedAt) || "N/A"}
                    </div>
                  </div>

                  {/* Card Number */}
                  <div className="space-y-1 pt-2 border-t border-gray-200">
                    <div className="text-sm text-gray-500">Card Number</div>
                    <div className="flex items-center space-x-2">
                      <CreditCard className="h-5 w-5 text-gray-600" />
                      <span className="font-mono text-lg text-gray-800">
                        {redemption.cardNumber}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-center space-x-4">
              <Button
                variant="outline"
                onClick={() => setIsSuccessModalOpen(false)}
                className="px-6"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setIsSuccessModalOpen(false);
                  setActiveTab("history");
                  if (!historyTabInitialized) {
                    fetchRedemptionHistory();
                    setHistoryTabInitialized(true);
                  }
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white px-6"
              >
                View Redemption History
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Details Modal */}
      <Dialog open={isHistoryModalOpen} onOpenChange={setIsHistoryModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <History className="h-5 w-5" />
              <span>Redemption Details</span>
            </DialogTitle>
          </DialogHeader>

          {selectedHistoryItem && (
            <div className="space-y-6">
              {/* Transaction Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">
                    ID
                  </Label>
                  <p className="font-semibold text-gray-900">
                    #{selectedHistoryItem.id}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Status
                  </Label>
                  <div>
                    {getStatusBadge(selectedHistoryItem.giftCardStatus)}
                  </div>
                </div>
              </div>

              {/* Card Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Card Number
                  </Label>
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-gray-500" />
                    <span className="font-mono text-sm">
                      {selectedHistoryItem.issuedCardNumber || "N/A"}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Amount Deducted
                  </Label>
                  <p className="font-semibold text-red-600">
                    {selectedHistoryItem.amountDeducted.toFixed(2)}{" "}
                    {localStorage.getItem("giftCardCurrency") || "USD"}
                  </p>
                </div>
              </div>

              {/* Mobile & Mall Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Mobile Number
                  </Label>
                  <p className="font-medium text-gray-900">
                    {selectedHistoryItem.mobileNo || "N/A"}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Mall ID
                  </Label>
                  <p className="font-medium text-gray-900">
                    {selectedHistoryItem.mallId}
                  </p>
                </div>
              </div>

              {/* Brand Info & Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Brand Name
                  </Label>
                  <p className="font-medium text-gray-900">
                    {selectedHistoryItem.merchantName ||
                      selectedHistoryItem.merchantCode ||
                      "N/A"}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-500">
                    Date & Time
                  </Label>
                  <div>
                    <p className="text-sm font-medium">
                      {getFormattedDateTime(selectedHistoryItem.redemedDate) ||
                        "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setIsHistoryModalOpen(false)}
                  className="px-6"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RedeemGiftCard;
